/**
 * AI処理関連のMock APIハンドラ
 */

import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { of, throwError } from 'rxjs';

import { setMutableProjectStatus } from './projects.handler';
import { USAGE_LIMIT_EXCEEDED_CODE } from '../../core/utils/http-error.utils';
import { findExhaustedAiUsages } from '../mock-data/ai-usage.mock';

import type { AiProcessApiResponse } from '../../features/ai-process/ai-process.models';
import type { AiUsageItemApiResponse } from '../../features/ai-usage/ai-usage.models';
import type { Observable } from 'rxjs';

/**
 * AI 実行が AI 利用量の上限超過で拒否されたときの problem+json body
 * （openapi の UsageLimitExceededProblem。Issue #475 Phase 2）。
 *
 * `code` は status ではなくこちらで判別させるための安定判別子。
 * `exceeded` は「どの軸が超過したか」の内訳で、内訳不明でもキー自体は必ず載る（空配列）。
 */
type UsageLimitExceededProblemBody = {
  title: string;
  status: number;
  detail: string;
  instance: string;
  code: typeof USAGE_LIMIT_EXCEEDED_CODE;
  exceeded: AiUsageItemApiResponse[];
};

/**
 * 上限超過の固定 detail（api-server の `USAGE_LIMIT_EXCEEDED_DETAIL` と同文言）。
 *
 * 予算超過は **追加付与まで解消しない恒久エラー** なので、
 * リトライを示唆する文言は入れない（クライアントに自動再送の導線を作らないため）。
 */
const USAGE_LIMIT_EXCEEDED_DETAIL =
  'The AI usage limit for this tenant has been exceeded. AI review cannot be executed until additional usage is granted.';

/** AI 審査実行が消費する計量軸（この軸が上限到達なら実行を拒否する） */
const AI_REVIEW_USAGE_TYPE = 'execution_cost_usd';

/**
 * AI処理リクエストをハンドリング
 * @param method HTTPメソッド
 * @param path テナントプレフィックス除去済みの短縮パス
 * @param _body リクエストボディ
 * @param tenantId テナントID（利用量の上限判定に使う）
 * @returns レスポンス（該当しない場合はnull）
 */
export function handleAiProcessRequest(
  method: string,
  path: string,
  _body: unknown,
  tenantId: string,
): Observable<HttpResponse<unknown>> | null {
  // POST /projects/:id/ai-process/execute
  const executeMatch = /^\/projects\/([^/]+)\/ai-process\/execute$/.exec(path);
  if (executeMatch && method === 'POST') {
    const projectId = executeMatch[1];
    return handleExecuteAiProcess(tenantId, projectId, path);
  }

  // POST /projects/:id/ai-process/cancel
  const cancelMatch = /^\/projects\/([^/]+)\/ai-process\/cancel$/.exec(path);
  if (cancelMatch && method === 'POST') {
    const projectId = cancelMatch[1];
    return of(aiProcessHandlers.cancelAiProcess(projectId));
  }

  return null;
}

/**
 * POST /api/v0/tenants/:tenantId/projects/:id/ai-process/execute
 *
 * AI サーバーが `402 Budget Exceeded` を返すケースを模擬する。Mock には AI サーバーが
 * 居ないため、保存済みの利用量スナップショットで実行費用の軸が上限到達
 * （remaining <= 0）していれば 422 を返す。それ以外は 202 Accepted。
 */
function handleExecuteAiProcess(
  tenantId: string,
  projectId: string,
  path: string,
): Observable<HttpResponse<unknown>> {
  const exhausted = findExhaustedAiUsages(tenantId);
  const isAiReviewBlocked = exhausted.some(
    (usage) => usage.type === AI_REVIEW_USAGE_TYPE,
  );

  if (isAiReviewBlocked) {
    return createUsageLimitExceededResponse(tenantId, path, exhausted);
  }

  return of(aiProcessHandlers.executeAiProcess(projectId));
}

/**
 * 上限超過の 422 を error channel に流す。
 *
 * Angular HttpClient は interceptor が返した `HttpResponse` を status に関わらず
 * 成功として扱うため、エラー応答は `throwError` + `HttpErrorResponse` で流す必要がある
 * （`document-types.handler.ts` と同じ方式）。
 */
function createUsageLimitExceededResponse(
  tenantId: string,
  path: string,
  exceeded: AiUsageItemApiResponse[],
): Observable<HttpResponse<unknown>> {
  const body: UsageLimitExceededProblemBody = {
    title: 'Unprocessable Entity',
    status: 422,
    detail: USAGE_LIMIT_EXCEEDED_DETAIL,
    instance: `/api/v0/tenants/${tenantId}${path}`,
    code: USAGE_LIMIT_EXCEEDED_CODE,
    exceeded,
  };

  return throwError(
    () =>
      new HttpErrorResponse({
        status: 422,
        statusText: 'Unprocessable Entity',
        error: body,
      }),
  );
}

/**
 * AI処理関連のMock APIハンドラ
 */
export const aiProcessHandlers = {
  /**
   * POST /api/v0/tenants/:tenantId/projects/:id/ai-process/execute
   * AI処理実行（202 Accepted）
   */
  executeAiProcess: (projectId: string): HttpResponse<AiProcessApiResponse> => {
    // ポーリング動作確認用: aiProcessStatus を processing にセットする。
    // 以降の GET /projects/:id で 3 回目以降に completed へ自動遷移する
    // （extractionStatus は変更しない想定で completed を指定）。
    setMutableProjectStatus(projectId, 'processing', 'completed');
    return new HttpResponse({
      status: 202,
      body: {
        message: 'AI処理を開始しました',
        aiProcessStatus: 'processing',
      },
    });
  },

  /**
   * POST /api/v0/tenants/:tenantId/projects/:id/ai-process/cancel
   * AI処理キャンセル（202 Accepted）
   */
  cancelAiProcess: (projectId: string): HttpResponse<AiProcessApiResponse> => {
    // ポーリング動作確認用: aiProcessStatus を cancelled にセットする。
    // processing 以外は自動遷移しないため、以降の GET でも cancelled を返し続ける。
    setMutableProjectStatus(projectId, 'cancelled', 'completed');
    return new HttpResponse({
      status: 202,
      body: {
        message: 'AI処理のキャンセルを受け付けました',
        aiProcessStatus: 'cancelled',
      },
    });
  },
};
